import { getRequiredMessage } from '@monetize/types/common';
import * as z from 'zod';
import { AddressSchema } from './address.types';
import { CustomFieldRecordSchema } from './customFields.types';

export enum LegalEntityAddressFormatEnum {
  US_FORMAT = 'US_FORMAT',
  EUROPEAN_FORMAT = 'EUROPEAN_FORMAT',
}
export const LegalEntityAddressFormatEnumZ = z.enum(
  LegalEntityAddressFormatEnum,
);

export const LegalEntityOptionsSchema = z
  .object({
    invoiceTerms: z
      .object({
        currencyTerms: z.array(
          z.object({
            active: z.boolean(),
            currencies: z.array(z.string()).min(1, {
              error: 'At least one currency must be selected',
            }),
            terms: z.string().min(1, {
              error: 'Terms content is required',
            }),
          }),
        ),
      })
      .optional(),
  })
  .nullable();

export const LegalEntityResponseSchema = AddressSchema.omit({
  id: true,
  description: true,
}).extend({
  id: z.string(),
  createDate: z.string(),
  createdBy: z.string(),
  customId: z.string().nullable(),
  defaultEntity: z.boolean(),
  lastModifiedBy: z.string(),
  logoStorageId: z.string().nullable(),
  modifyDate: z.string(),
  name: z.string(),
  email: z.string(),
  vatNumber: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  addressFormat: LegalEntityAddressFormatEnumZ.optional(),
  quoteTemplate: z.string().nullish(),
  options: LegalEntityOptionsSchema,
  customFields: CustomFieldRecordSchema.optional(),
});

export type ILegalEntityResponseSchema = z.infer<
  typeof LegalEntityResponseSchema
>;

const ACCEPTED_FILE_TYPES = ['image/png'];

export const LegalEntityRequestSchema = AddressSchema.omit({
  id: true,
  description: true,
}).extend({
  file: z
    .custom<File>((val) => val instanceof File, 'Please upload a file')
    .refine((file) => ACCEPTED_FILE_TYPES.includes(file?.type), {
      error: 'Please choose png format image only',
    })
    .optional(),
  name: z.string().min(1, { message: getRequiredMessage('Name') }),
  customId: z.string().optional(),
  addressFormat: LegalEntityAddressFormatEnumZ.optional(),
  email: z
    .email({
      error: 'Invalid email address',
    })
    .nullish()
    .or(z.literal('')),
  vatNumber: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  quoteTemplate: z.string().nullish(),
  options: LegalEntityOptionsSchema.optional(),
  customFields: CustomFieldRecordSchema.optional(),
});

export type ILegalEntityRequestSchema = z.infer<
  typeof LegalEntityRequestSchema
>;

/**
 * Legal entity data gets stamped to quotes and invoices
 */
export const LegalEntitySnapshot = z.object({
  legalEntityId: z.string(),
  name: z.string().nullable(),
  customId: z.string().nullish(),
  email: z.string().nullish(),
  vatNumber: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  logoStorageId: z.string().nullish(),
  addressFormat: LegalEntityAddressFormatEnumZ.nullish(),
  address: AddressSchema.omit({ id: true, description: true }),
});

export type ILegalEntitySnapshot = z.infer<typeof LegalEntitySnapshot>;

/** Used in places where a summary snaphot is provided */
export const LegalEntitySnapshotSlim = LegalEntitySnapshot.pick({
  legalEntityId: true,
  name: true,
}).extend({
  id: z.string(),
});
export type ILegalEntitySnapshotSlim = z.infer<typeof LegalEntitySnapshotSlim>;
